import { lazy } from "react";
import {
  decideStaleChunkReload,
  type StaleChunkReloadRecord,
} from "~/lib/stale-chunk-reload-policy";

const STALE_CHUNK_RELOAD_KEY = "stale-chunk-reload";

// A single stale deploy fails many imports at once (the page chunk plus its
// icon/vendor dep chunks), each calling reloadForStaleChunk. This in-memory flag
// makes the reload count exactly one per actual page load, so a burst does not
// spend the whole retry budget before the page even reloads. It resets to false
// on the fresh page after a reload.
let reloadScheduledThisPage = false;

const isReloadRecord = (value: unknown): value is StaleChunkReloadRecord =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as Record<string, unknown>).count === "number" &&
  typeof (value as Record<string, unknown>).firstAt === "number";

const readReloadRecord = (): StaleChunkReloadRecord | null => {
  const raw = window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return isReloadRecord(parsed) ? parsed : null;
  } catch {
    // @expected-error-boundary corrupt/legacy stored value; treat as no record so a fresh window can open.
    return null;
  }
};

/**
 * Reload the page to pick up fresh hashed assets after a deploy made the
 * currently-loaded chunks stale (404). A single reload is not enough during a
 * zero-downtime deploy where old and new instances briefly co-serve traffic and
 * a reload can land back on the version missing the chunk — so we allow a small,
 * time-boxed budget of reloads with backoff (see `stale-chunk-reload-policy`)
 * before giving up. Persisted in sessionStorage so the budget survives reloads.
 *
 * Returns true when a reload was scheduled, false otherwise (dev mode, budget
 * spent for this window, or sessionStorage unavailable). Callers use the return
 * value to decide whether to swallow the error or surface a fallback.
 */
export const reloadForStaleChunk = (): boolean => {
  if (!import.meta.env.PROD) {
    return false;
  }

  // Already reloading from an earlier failure in this same page load: report
  // handled (so the caller swallows the error) without counting it again.
  if (reloadScheduledThisPage) {
    return true;
  }

  try {
    const decision = decideStaleChunkReload(readReloadRecord(), Date.now());

    if (!decision.shouldReload) {
      return false;
    }

    reloadScheduledThisPage = true;
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, JSON.stringify(decision.nextRecord));

    if (decision.delayMs > 0) {
      window.setTimeout(() => window.location.reload(), decision.delayMs);
    } else {
      window.location.reload();
    }

    return true;
  } catch {
    // @expected-error-boundary sessionStorage unavailable (private mode/quota); let the original error surface.
    return false;
  }
};

/**
 * A dynamic `import()` of a hash-named chunk that no longer exists on the server
 * fails in browser-specific ways. Chromium rejects with "Failed to fetch
 * dynamically imported module"; WebKit/Safari reports "Importing a module
 * script failed" (and sometimes resolves the namespace to `undefined` instead
 * of rejecting at all — handled separately at the call sites).
 */
export const isDynamicImportError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");

  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload (css|module)/i.test(
    message,
  );
};

const NEVER_RESOLVES = <T>(): Promise<T> => new Promise<T>(() => {});

/**
 * Wraps a dynamic-import promise (page or lazy component) so a stale-chunk
 * failure triggers a one-time reload to fresh assets instead of crashing.
 * Handles both the rejection path and the WebKit "resolved to undefined" path.
 */
export const recoverDynamicImport = <T>(load: () => Promise<T>): Promise<T> =>
  load().then(
    (module) => {
      // WebKit can resolve a failed module preload to an undefined namespace
      // instead of rejecting; React.lazy then crashes reading `_result.default`
      // (its `_result` is the undefined module). Treat that as a stale chunk.
      if (module) {
        return module;
      }

      if (reloadForStaleChunk()) {
        return NEVER_RESOLVES<T>();
      }

      return module;
    },
    (error: unknown) => {
      if (isDynamicImportError(error) && reloadForStaleChunk()) {
        return NEVER_RESOLVES<T>();
      }

      throw error;
    },
  );

/**
 * Drop-in replacement for `React.lazy` that recovers from stale-chunk failures
 * after a deploy instead of falling through to an error boundary. Mirrors
 * React's own `lazy` typing so it accepts memo/forwardRef components too.
 */
export const lazyWithReload: typeof lazy = (load) => lazy(() => recoverDynamicImport(load));

window.addEventListener("vite:preloadError", (event) => {
  if (reloadForStaleChunk()) {
    event.preventDefault();
  }
});

// A stale-chunk failure can also surface as an unhandled promise rejection that
// no React error boundary can catch: Inertia's page `resolve()` rejects
// asynchronously (initial load or a client-side visit) before any component
// renders. Without this, that rejection leaves a blank screen. Recover the same
// way — a bounded reload to fresh assets — and swallow the console noise only
// when we actually acted on it.
window.addEventListener("unhandledrejection", (event) => {
  if (isDynamicImportError(event.reason) && reloadForStaleChunk()) {
    event.preventDefault();
  }
});
